import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
import { AuthResponse } from '../types';
import './AuthPage.css';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result: AuthResponse = await authService.login(formData.username, formData.password);
      const user = result.user;

      // Update global auth state immediately
      if (user) {
        login(user);
      }

      // Admin: chuyển đến trang admin luôn
      if (user?.role === 'admin') {
        navigate('/admin/users', { replace: true });
        return;
      }

      // User thường: reset trạng thái đóng banner để đảm bảo hiển thị lại khi mới login
      sessionStorage.removeItem('eventBannerClosed');
      navigate('/community', { replace: true });
    } catch (err: any) {
      let errorMessage = err.response?.data?.error;
      const errorKey = errorMessage;
      const isLocked = err.response?.data?.is_locked || false;
      const remainingAttempts = err.response?.data?.remaining_attempts;

      // Translate error keys
      if (errorMessage && typeof errorMessage === 'string' && errorMessage.startsWith('AUTH_')) {
        if (errorMessage === 'AUTH_PASSWORD_WRONG_REMAINING') {
          errorMessage = t('auth.errors.AUTH_PASSWORD_WRONG_REMAINING', { remaining_attempts: remainingAttempts });
        } else {
          errorMessage = t(`auth.errors.${errorMessage}`);
          // Fallback if translation fails (returns key)
          if (errorMessage === `auth.errors.${errorKey}`) {
            // If rate limit, use common
            if (errorKey === 'AUTH_RATE_LIMIT') {
              errorMessage = t('common.tooManyRequests') || 'Too many requests';
            } else {
              errorMessage = errorKey;
            }
          }
        }
      } else {
        // Fallback for legacy or text errors
        errorMessage = errorMessage || t('auth.fail');
      }

      // Legacy fallback: if still 'Invalid credentials' (shouldn't happen with updated backend)
      if (errorMessage === 'Invalid credentials') {
        errorMessage = t('auth.invalidCreds');
      }

      setError(errorMessage);

      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('.error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };


  // Render form login bình thường
  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Illustration Side */}
        <div className="auth-illustration">
          <div className="auth-illustration-content">
            <div className="auth-illustration-icon">
              <img src="/images/logo.png" alt="TaoThaoAI Logo" />
            </div>
            <h1>{t('auth.welcomeBack')}</h1>
            <p>
              {t('auth.loginSubtitle')}
            </p>
          </div>
        </div>

        {/* Form Side */}
        <div className="auth-container">
          <div className="auth-header">
            <h2>{t('auth.loginTitle')}</h2>
            <p>{t('auth.loginDesc')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">{t('auth.usernameLabel')}</label>
              <div className={`input-wrapper ${formData.username ? 'has-value' : ''}`}>
                <span className="input-icon">👤</span>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder={t('auth.usernamePlaceholder')}
                  required
                  title={t('common.validation.required')}
                  onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('common.validation.required'))}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.passwordLabel')}</label>
              <div className={`input-wrapper ${formData.password ? 'has-value' : ''}`}>
                <span className="input-icon">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  title={t('common.validation.required')}
                  onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('common.validation.required'))}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#a0aec0',
                    fontSize: '1.2rem',
                    zIndex: 1,
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? t('auth.loggingIn') : t('auth.loginButton')}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              <Link to="/forgot-password" className="auth-footer-link">
                {t('auth.forgotPassword')}
              </Link>
            </p>
            <p>
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="auth-footer-link">
                {t('auth.registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
